/* Public storefront pages are warmed before the entrance light reveals them.
   Contact, cart and product forms keep their native Shopify navigation. */
(() => {
  const root = document.documentElement;
  const base = new URL(document.body.dataset.shopRoot || '/', location.origin);
  const views = new Set(['fine-jewelry','hammered','lotus-circle-ring','abstract-cluster','gold-turquoise-signet','silver-turquoise-signet']);
  const pages = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let navigating = false;
  let sequence = 0;
  let revealTimer;
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const canHandle = value => {
    const url = new URL(value, location.href);
    return url.origin === location.origin && url.pathname === base.pathname &&
      (!url.searchParams.has('view') || views.has(url.searchParams.get('view'))) &&
      [...url.searchParams.keys()].every(key => ['view','entrance','_ab','_fd','_sc','pb','preview_theme_id'].includes(key));
  };
  const pageKey = url => url.pathname + '?' + (url.searchParams.get('view') || 'home');
  const warmImages = async main => {
    const sources = [...main.querySelectorAll('img')].slice(0,4);
    await Promise.race([Promise.all(sources.map(source => new Promise(resolve => {
      const image = new Image();
      image.onload = () => image.decode?.().catch(() => {}).then(resolve) || resolve();
      image.onerror = resolve;
      image.src = source.currentSrc || source.getAttribute('src') || '';
      if (image.complete) resolve();
    }))), wait(1800)]);
  };
  const prepare = value => {
    const url = new URL(value, location.href);
    if (!canHandle(url)) return Promise.resolve(null);
    const key = pageKey(url);
    if (pages.has(key)) return pages.get(key);
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5500);
      try {
        const response = await fetch(url.pathname + url.search, {credentials:'same-origin', signal:controller.signal, headers:{Accept:'text/html'}});
        if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Page unavailable');
        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const main = page.getElementById('main');
        if (!main || !page.querySelector('#StoreContent')) throw new Error('Unsupported page');
        await warmImages(main);
        return {page, main};
      } finally { clearTimeout(timeout); }
    })().catch(() => { pages.delete(key); return null; });
    pages.set(key, promise);
    return promise;
  };
  const paintDensity = () => {
    const value = root.dataset.catalogDensity || 'comfortable';
    document.querySelectorAll('button[data-catalog-density]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.catalogDensity === value));
    });
  };
  try { root.dataset.catalogDensity = sessionStorage.getItem('nd-catalog-density') || 'comfortable'; } catch (_) { root.dataset.catalogDensity = 'comfortable'; }
  paintDensity();
  document.addEventListener('click', event => {
    const button = event.target.closest('button[data-catalog-density]');
    if (!button || !['comfortable','compact'].includes(button.dataset.catalogDensity)) return;
    root.dataset.catalogDensity = button.dataset.catalogDensity;
    try { sessionStorage.setItem('nd-catalog-density', root.dataset.catalogDensity); } catch (_) {}
    paintDensity();
  });
  const mount = async (value, options = {}) => {
    const url = new URL(value, location.href);
    const result = await prepare(url);
    if (!result) return false;
    const main = document.getElementById('main');
    if (!main) return false;
    main.replaceChildren(...[...result.main.childNodes].map(node => document.importNode(node, true)));
    document.body.classList.toggle('nd-cloud-home', result.page.body.classList.contains('nd-cloud-home'));
    document.title = result.page.title;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = url.origin + url.pathname + url.search;
    document.querySelectorAll('.mobile-menu nav a').forEach(link => {
      const target = new URL(link.href);
      if (target.pathname === url.pathname && target.searchParams.get('view') === url.searchParams.get('view')) link.setAttribute('aria-current','page');
      else link.removeAttribute('aria-current');
    });
    if (options.history !== false) history.pushState({...(history.state || {}),ndBoutique:true}, '', url);
    paintDensity();
    document.dispatchEvent(new CustomEvent('nd:page-ready'));
    return true;
  };
  const reveal = id => {
    clearTimeout(revealTimer);
    root.classList.remove('nd-page-leaving');
    root.classList.add('nd-page-cover');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('nd-page-reveal')));
    revealTimer = setTimeout(() => {
      if (id !== sequence) return;
      root.classList.remove('nd-page-cover','nd-page-reveal','nd-entrance-arrival');
      navigating = false;
    },950);
  };
  const navigate = async (value, options = {}) => {
    if (navigating) return;
    navigating = true;
    const id = ++sequence;
    clearTimeout(revealTimer);
    root.classList.remove('nd-page-cover','nd-page-reveal','nd-entrance-arrival');
    const url = new URL(value, location.href);
    const ready = prepare(url);
    if (!motion.matches) root.classList.add('nd-page-leaving');
    await Promise.all([ready, motion.matches ? Promise.resolve() : wait(390)]);
    if (id !== sequence) return;
    if (!await mount(url,options)) { location.assign(url); return; }
    const entrance = document.querySelector('nd-store-entrance');
    if (entrance) { entrance.hidden = true; entrance.syncMotion?.(); }
    const content = document.getElementById('StoreContent');
    if (content) content.hidden = false;
    const target = url.hash && document.getElementById(decodeURIComponent(url.hash.slice(1)));
    window.scrollTo({top:target ? Math.max(0,target.getBoundingClientRect().top + scrollY - 120) : 0,left:0,behavior:'instant'});
    document.getElementById('main')?.focus({preventScroll:true});
    if (!motion.matches) reveal(id);
    else { root.classList.remove('nd-page-leaving'); navigating = false; }
  };
  const warmLink = event => {
    const link = event.target.closest('a[data-soft-nav],a[data-box-destination]');
    if (link && canHandle(link.href)) prepare(link.href);
  };
  document.addEventListener('pointerover',warmLink,{passive:true});
  document.addEventListener('focusin',warmLink);
  document.addEventListener('keydown',event => {
    if (event.key !== 'Escape' || !navigating) return;
    sequence++; navigating = false;
    clearTimeout(revealTimer);
    root.classList.remove('nd-page-leaving','nd-page-cover','nd-page-reveal');
  });
  window.addEventListener('popstate', () => {
    sequence++; navigating = false;
    clearTimeout(revealTimer);
    root.classList.remove('nd-page-leaving','nd-page-cover','nd-page-reveal','nd-entrance-arrival');
    if (!canHandle(location.href)) { location.reload(); return; }
    if (new URLSearchParams(location.search).get('entrance') === '1') {
      document.querySelector('nd-store-entrance')?.reopen();
    } else navigate(location.href,{history:false});
  });
  window.NoelDrewShop = {
    canHandle, prepare, mount, navigate,
    readyMain() { return Promise.race([Promise.all([warmImages(document.getElementById('main')),document.fonts?.ready || Promise.resolve()]),wait(2200)]); },
    warmCollections() { for (const view of ['fine-jewelry','hammered']) { const url = new URL(base); url.searchParams.set('view',view); prepare(url); } }
  };
})();
